import { AUTO_REPLY_DEFAULT_MESSAGE } from '@chat-emp/shared';
import { useSyncExternalStore } from 'react';

import { getAutoReply, saveAutoReply } from '../lib/facade.js';
import { sameReply, type AutoReply } from './AutoReply.js';

/**
 * The account's auto reply: fetch, hold, save — and hold the draft too.
 *
 * On the module rather than in a provider for the reason `useEvents` is: both
 * halves of the section are looking at one value. Here it goes further than
 * sharing a fetch. The settings column and the preview mount into two different
 * `ChatLayout` slots (`inbox` and `conversation`), so the draft has to live
 * somewhere both can read it from — typing must redraw the preview on the next
 * keystroke, and a draft kept in the settings column would leave the pane
 * showing the last saved version three inches away.
 *
 * `reply` is null until the first read lands, so the pane can tell *off* from
 * *not loaded* and never flashes a disabled switch at somebody whose auto reply
 * is on.
 */

/** What the server last told us is stored. Null until the first read lands. */
let stored: AutoReply | null = null;
/** The edit in progress, or null when the draft is just the stored reply. */
let edit: AutoReply | null = null;
let loading = true;
let failed = false;
let saving = false;
let hydrated = false;
const listeners = new Set<() => void>();

export interface UseAutoReply {
  /** What is stored. Null until the first read lands. */
  reply: AutoReply | null;
  /** What is on screen: the edit if there is one, otherwise the stored reply. */
  draft: AutoReply | null;
  /** Whether there is anything to save. Drives the button — a `saving` flag says
   *  nothing about whether saving is *needed*. */
  dirty: boolean;
  loading: boolean;
  failed: boolean;
  saving: boolean;
  setDraft: (reply: AutoReply) => void;
  /** Resolves true when the reply landed, false when it did not. */
  save: () => Promise<boolean>;
}

// One object per state change, not per read: useSyncExternalStore compares
// snapshots by identity, and a fresh literal on every getSnapshot is an infinite
// re-render.
let view = snapshot();

function snapshot(): UseAutoReply {
  const draft = edit ?? stored;
  return {
    reply: stored,
    draft,
    dirty: stored !== null && draft !== null && !sameReply(stored, draft),
    loading,
    failed,
    saving,
    setDraft,
    save,
  };
}

function publish(): void {
  view = snapshot();
  for (const listener of listeners) listener();
}

function setDraft(reply: AutoReply): void {
  edit = reply;
  publish();
}

/**
 * Whether this account has an auto reply at all yet.
 *
 * All three fields at their zero value, not just the empty message: somebody who
 * cleared the wording but kept the switch on, or kept the form, has an auto
 * reply they are in the middle of — and putting the default back under them is
 * the one place this could overwrite an intention.
 *
 * It cannot tell "never written" from "written, then cleared and switched off",
 * because the row upstream cannot either — a read answers the same zero value
 * for both. So an employer who deliberately keeps it blank and off meets the
 * suggestion again on their next load. It is a draft in a box they can leave, so
 * the cost of guessing wrong is a sentence to delete rather than a message sent.
 */
function untouched(reply: AutoReply): boolean {
  return !reply.enabled && reply.message.trim() === '' && reply.formId === null;
}

async function hydrate(): Promise<void> {
  try {
    stored = await getAutoReply();
    // Open on the suggested wording rather than an empty box. As an edit, which
    // is what keeps it honest: `reply` stays the zero value the BFF answered,
    // the pane opens dirty with Save live, and nothing goes out to anybody until
    // that button is pressed. Switched off, too — writing a message for somebody
    // is a suggestion, turning it on for them would be sending it.
    if (untouched(stored)) edit = { ...stored, message: AUTO_REPLY_DEFAULT_MESSAGE };
    failed = false;
  } catch {
    // Let a later mount try again. The pane shows its error state until then
    // rather than an auto reply that is off, which would be a lie — and one the
    // employer could act on by switching a switch that is already on.
    stored = null;
    failed = true;
    hydrated = false;
  } finally {
    loading = false;
    publish();
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  if (!hydrated) {
    hydrated = true;
    loading = true;
    failed = false;
    publish();
    void hydrate();
  }
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Write the draft, and take the server's answer as the new stored reply.
 *
 * The echo, not the draft: the route trims the message before it stores it, so
 * adopting what was sent would show the employer their own draft and call it
 * saved — and leave Save enabled forever over a difference of two spaces.
 *
 * The edit is dropped only on success. A failed save leaves the draft exactly as
 * it was typed, because the alternative is telling somebody their wording is
 * gone as well as unsaved.
 */
async function save(): Promise<boolean> {
  const draft = edit ?? stored;
  if (!draft || saving) return false;
  saving = true;
  publish();
  try {
    stored = await saveAutoReply(draft);
    edit = null;
    return true;
  } catch {
    return false;
  } finally {
    saving = false;
    publish();
  }
}

/**
 * What a gated read answers: nothing to edit, settled, unfailed. One frozen
 * object for the same reason `view` is one — the snapshot is compared by identity.
 */
const OFF: UseAutoReply = {
  reply: null,
  draft: null,
  dirty: false,
  loading: false,
  failed: false,
  saving: false,
  setDraft,
  save,
};

/** Subscribing to nothing: an account without the feature has no reply to hear about. */
const ignore = (): (() => void) => () => {};

/**
 * @param enabled whether this account has the Auto reply feature *and* is
 * looking at the section. Required, not defaulted: an ungated call would fetch,
 * the BFF would answer 403 (it gates the same row server-side), and every boot
 * would carry a guaranteed failed request. There is no reading to be had, so
 * there is no request to make.
 */
export function useAutoReply(enabled: boolean): UseAutoReply {
  return useSyncExternalStore(
    enabled ? subscribe : ignore,
    () => (enabled ? view : OFF),
    () => (enabled ? view : OFF),
  );
}

/** Test seam: drop the store so each case starts from nothing. */
export function resetAutoReplyForTest(): void {
  stored = null;
  edit = null;
  loading = true;
  failed = false;
  saving = false;
  hydrated = false;
  listeners.clear();
  view = snapshot();
}
